import View from '@play-co/timestep-core/lib/ui/View';
import ImageView from '@play-co/timestep-core/lib/ui/ImageView';
import ImageScaleView from '@play-co/timestep-core/lib/ui/ImageScaleView';

import i18n from 'src/lib/i18n/i18n';
import StateObserver from 'src/StateObserver';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import bitmapFonts from 'src/lib/bitmapFonts';
import uiConfig from 'src/lib/ui/config';
import { parseAmount } from 'src/lib/utils';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';

import { Admin } from 'src/replicant/admin';
import PopupBasic from '../components/popups/PopupBasic';

type IconStyleConfig = {
  x: number;
  y: number;
  defaultHeight: number;
  defaultWidth: number;
};

const defaultIconStyle = {
  x: -31,
  y: -6,
  defaultHeight: 72,
  defaultWidth: 72,
};

const skin = {
  opts: {
    width: 591,
    height: 560,
  },
  title: {},
  message: {
    x: 10,
    y: 100,
    height: 100,
    size: 30,
    color: '#9A7171',
    font: bitmapFonts('Body'),
    align: 'center' as const,
    verticalAlign: 'center' as const,
    wrappedWidth: 20,
  },
  coins: {
    y: 234,
    icon: 'assets/ui/hud/icons/hud_coin.png',
    iconProperties: {
      ...defaultIconStyle,
    },
  },
  gems: {
    y: 334,
    icon: 'assets/ui/hud/icons/hud_gem.png',
    iconProperties: {
      ...defaultIconStyle,
    },
  },
  button: {
    ...uiConfig.buttons.secondary,
    y: 477,
    width: 259,
    height: 80,
    centerOnOrigin: true,
    font: bitmapFonts('Title'),
    fontSize: 31,
    labelOffsetY: -1,
  },
  barDefaults: {
    frame: {
      x: 90,
      height: 62,
      wrappedWidth: 160,
    },
    caption: {
      y: 28,
      height: 33,
      centerOnOrigin: true,
      font: bitmapFonts('Title'),
      size: 36,
      color: 'white',
      align: 'right' as const,
      verticalAlign: 'center' as const,
      wordWrap: false,
      wrappedWidth: 50,
    },
  },
};

function sign(value: number) {
  if (!value) {
    return '';
  }

  return value > 0 ? '+' : '-';
}

function createBar(opts: {
  superview: View;
  icon: string;
  y: number;
  iconProperties: IconStyleConfig;
}) {
  const frame = new ImageScaleView({
    ...uiConfig.hud.coinFrame,
    ...skin.barDefaults.frame,
    superview: opts.superview,
    y: opts.y,
    width: opts.superview.style.width - skin.barDefaults.frame.wrappedWidth,
  });

  const icon = new ImageView({
    superview: frame,
    autoSize: true,
    image: opts.icon,
  });

  icon.updateOpts({
    ...opts.iconProperties,
    width:
      opts.iconProperties.defaultWidth * (icon.style.width / icon.style.height),
    height: opts.iconProperties.defaultHeight,
  });

  const caption = new LangBitmapFontTextView({
    superview: frame,
    ...skin.barDefaults.caption,
    x: frame.style.width / 2,
    width: frame.style.width - skin.barDefaults.caption.wrappedWidth,
  });

  return {
    setValue: (value: number) => {
      caption.localeText = () => sign(value) + parseAmount(Math.abs(value));
    },
  };
}

export default class PopupAdminReimburse extends PopupBasic {
  private coins: ReturnType<typeof createBar>;
  private gems: ReturnType<typeof createBar>;

  constructor(opts: { superview: View; close: () => void }) {
    super({
      ...opts,
      ...skin.opts,
      darkerBg: false,
      tracking: { feature: 'admin', $subFeature: 'popup-admin-reimburse' },
    });

    this.buttonClose.hide();
    this.title.updateOpts(skin.title);

    new LangBitmapFontTextView({
      superview: this.box,
      ...skin.message,
      width: this.box.style.width - skin.message.wrappedWidth,
      localeText: () => i18n('admin.reimburse.message'),
    });

    this.coins = createBar({
      superview: this.box,
      ...skin.coins,
    });

    this.gems = createBar({
      superview: this.box,
      ...skin.gems,
    });

    new ButtonScaleViewWithText({
      superview: this.box,
      ...skin.button,
      x: this.box.style.width / 2,
      localeText: () => i18n('basic.okay'),
      onClick: async () => opts.close(),
      tracking: { feature: 'admin', $subFeature: 'popup-admin-reimburse-ok' },
    });
  }

  init() {
    super.init({
      title: i18n('admin.reimburse.title'),
    });

    const { user } = StateObserver.getState();

    const reimbursements = Admin.getters(user).getReimbursements();

    this.coins.setValue(reimbursements.coins);
    this.gems.setValue(reimbursements.gems);
  }

  onPopupClosing() {
    void StateObserver.invoke.claimAdminMessageResources();
  }
}
